/**
 * BigSplice Chat Message Component
 * Renders individual chat messages with rich content support
 */

import React from 'react';
import { ChatMessage as ChatMessageType } from '../../hooks/useChatState';
import { ChatVisualization } from './ChatVisualization';
import { ChatResultsTable } from './ChatResultsTable';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import {
  User,
  Bot,
  Loader2,
  AlertCircle,
  FileText,
  Folder,
  Upload,
  HelpCircle,
  BarChart3,
  Database,
  ExternalLink,
} from 'lucide-react';
import { cn } from '../../lib/utils';

// Inline type definitions
interface TableResult {
  columns: string[];
  rows: Record<string, unknown>[];
  totalRows?: number;
  truncated?: boolean;
  query?: string;
}

interface FileListResult {
  files: Array<{
    name: string;
    size: number;
    modified: string;
    type: 'file' | 'folder';
  }>;
  path?: string;
  currentFolder?: string;
  totalCount?: number;
}

interface VisualizationResult {
  type: 'bar' | 'donut' | 'pie' | 'scatter' | 'line';
  data: Array<Record<string, string | number | boolean | null>>;
  title?: string;
  layout?: 'bars' | 'donut' | 'pie' | 'scatter' | 'spiral' | 'grid';
  xAxis?: string;
  colorColumn?: string;
  config?: {
    xAxis?: string;
    yAxis?: string;
    colorBy?: string;
    layout?: string;
  };
}

interface HelpResult {
  commands: Array<{
    name: string;
    commands: Array<{
      command: string;
      description: string;
      example: string;
    }>;
  }>;
}

interface UsageResult {
  queriesRun?: number;
  filesAnalyzed?: number;
  storageUsed?: string;
  lastActive?: string;
  storage?: {
    used: number;
    limit: number;
    percentage: number;
  };
  queries?: {
    daily: number;
    monthly: number;
  };
  tier?: string;
}

interface FileSelectionResult {
  files: Array<{
    name: string;
    size: number;
    s3Key: string;
    isFolder?: boolean;
  }>;
  action: string;
  layout?: string;
  groupBy?: string | null;
}

interface DeleteConfirmationState {
  step: 'button' | 'text' | 'confirmed';
  targetFile: string | null;
  targetType: 'file' | 'folder';
  typedText: string;
}

interface ChatMessageProps {
  message: ChatMessageType;
  deleteConfirmation: DeleteConfirmationState;
  onConfirmDeleteStep1: () => void;
  onConfirmDeleteStep2: (text: string) => Promise<boolean>;
  onCancelDelete: () => void;
  onUpdateDeleteTypedText: (text: string) => void;
  onOpenUploadHelper: (method?: 'direct' | 'url' | 's3' | 'sql') => void;
  onSelectFile?: (name: string, key: string) => void;
  onSendMessage?: (message: string) => void;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  deleteConfirmation,
  onConfirmDeleteStep1,
  onConfirmDeleteStep2,
  onCancelDelete,
  onUpdateDeleteTypedText,
  onOpenUploadHelper,
  onSelectFile,
  onSendMessage,
}) => {
  const isUser = message.role === 'user';
  const isLoading = message.status === 'sending';
  const isError = message.status === 'error';

  const renderContent = () => {
    // Loading state
    if (isLoading) {
      return (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Processing...</span>
        </div>
      );
    }

    // Error state
    if (isError && message.metadata?.error) {
      return (
        <div className="flex items-start gap-2 text-destructive">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{message.metadata.error}</span>
        </div>
      );
    }

    // Render based on result type
    switch (message.metadata?.resultType) {
      case 'table':
        return <TableContent data={message.metadata.resultData as TableResult} />;
      case 'file_list':
        return <FileListContent data={message.metadata.resultData as FileListResult} />;
      case 'visualization':
        return <VisualizationContent data={message.metadata.resultData as VisualizationResult} />;
      case 'help':
        return <HelpContent data={message.metadata.resultData as HelpResult} />;
      case 'usage':
        return <UsageContent data={message.metadata.resultData as UsageResult} />;
      case 'upload_guidance':
        return <UploadGuidanceContent onOpenHelper={onOpenUploadHelper} />;
      case 'file_selection':
        return (
          <FileSelectionContent
            data={message.metadata.resultData as FileSelectionResult}
            onSelectFile={onSelectFile}
            onSendMessage={onSendMessage}
          />
        );
      default:
        return <TextContent content={message.content} />;
    }
  };

  return (
    <div
      className={cn(
        'flex gap-3 py-4',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {/* Avatar */}
      <div
        className={cn(
          'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
          isUser ? 'bg-primary' : 'bg-muted'
        )}
      >
        {isUser ? (
          <User className="h-4 w-4 text-primary-foreground" />
        ) : (
          <Bot className="h-4 w-4" />
        )}
      </div>

      {/* Message Content */}
      <div
        className={cn(
          'flex-1 max-w-[85%]',
          isUser ? 'text-right' : 'text-left'
        )}
      >
        <div
          className={cn(
            'inline-block rounded-lg px-4 py-2',
            isUser
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted',
            isError && 'bg-destructive/10'
          )}
        >
          {renderContent()}
        </div>

        {/* Metadata */}
        {message.metadata?.processingLayer && !isUser && (
          <div className="mt-1 text-xs text-muted-foreground">
            {message.metadata.processingLayer === 'static' || message.metadata.processingLayer === 'bedrock'
              ? 'Processed in-engine'
              : 'Processed via AI'}
            {message.metadata.executionTime && (
              <span> ({message.metadata.executionTime}ms)</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Text content component
const TextContent: React.FC<{ content: string }> = ({ content }) => {
  return <div className="whitespace-pre-wrap">{content}</div>;
};

// Table content component
const TableContent: React.FC<{ data: TableResult }> = ({ data }) => {
  return (
    <div className="w-full">
      <ChatResultsTable
        columns={data.columns}
        rows={data.rows}
        totalRows={data.totalRows}
        truncated={data.truncated}
      />
      {data.query && (
        <div className="mt-2 text-xs text-muted-foreground">
          Query: <code className="bg-muted px-1 py-0.5 rounded">{data.query}</code>
        </div>
      )}
    </div>
  );
};

// File list content component
const FileListContent: React.FC<{ data: FileListResult }> = ({ data }) => {
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  return (
    <div className="w-full">
      <div className="text-sm font-medium mb-2">
        {data.currentFolder ? `Files in ${data.currentFolder}` : 'Your files'}
        <span className="text-muted-foreground ml-2">({data.totalCount} items)</span>
      </div>
      <div className="space-y-1 max-h-64 overflow-y-auto">
        {data.files.map((file, index) => (
          <div
            key={index}
            className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-background/50"
          >
            {file.type === 'folder' ? (
              <Folder className="h-4 w-4 text-yellow-500" />
            ) : (
              <FileText className="h-4 w-4 text-blue-500" />
            )}
            <span className="flex-1 truncate">{file.name}</span>
            {file.type !== 'folder' && (
              <span className="text-xs text-muted-foreground">
                {formatSize(file.size)}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Visualization content component
const VisualizationContent: React.FC<{ data: VisualizationResult }> = ({ data }) => {
  const layout = data.layout || 'bars';
  const chartData = data.data || [];

  if (chartData.length === 0) {
    return (
      <div className="w-full p-4 text-center text-muted-foreground">
        <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>No data available for visualization</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      {data.title && (
        <div className="text-sm font-medium mb-2">{data.title}</div>
      )}
      <ChatVisualization
        data={chartData}
        layout={layout}
        xAxis={data.xAxis}
        colorColumn={data.colorColumn}
      />
      <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
        <BarChart3 className="h-3 w-3" />
        <span>
          {layout} chart | {chartData.length} data points
        </span>
      </div>
    </div>
  );
};

// Help content component
const HelpContent: React.FC<{ data: HelpResult }> = ({ data }) => {
  return (
    <div className="w-full">
      <div className="text-sm font-medium mb-3 flex items-center gap-2">
        <HelpCircle className="h-4 w-4" />
        Available Commands
      </div>
      <div className="space-y-4">
        {data.commands.map((category, index) => (
          <div key={index}>
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
              {category.name}
            </div>
            <div className="space-y-1">
              {category.commands.map((cmd, cmdIndex) => (
                <div key={cmdIndex} className="text-sm">
                  <code className="bg-background/50 px-1 py-0.5 rounded text-primary">
                    {cmd.command}
                  </code>
                  <span className="text-muted-foreground ml-2">- {cmd.description}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Usage content component
const UsageContent: React.FC<{ data: UsageResult }> = ({ data }) => {
  return (
    <Card className="w-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Database className="h-4 w-4" />
          Usage Statistics
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Storage */}
        {data.storage && (
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span>Storage</span>
              <span className="text-muted-foreground">
                {(data.storage.used / (1024 * 1024 * 1024)).toFixed(2)} GB /{' '}
                {(data.storage.limit / (1024 * 1024 * 1024)).toFixed(0)} GB
              </span>
            </div>
            <Progress value={data.storage.percentage} className="h-2" />
          </div>
        )}
        {data.storageUsed && (
          <div className="text-sm">
            <span className="text-muted-foreground">Storage Used: </span>
            <span className="font-medium">{data.storageUsed}</span>
          </div>
        )}

        {/* Queries */}
        {data.queries && (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-muted-foreground">Daily Queries</div>
              <div className="font-medium">{data.queries.daily}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Monthly Queries</div>
              <div className="font-medium">{data.queries.monthly}</div>
            </div>
          </div>
        )}
        {data.queriesRun !== undefined && (
          <div className="text-sm">
            <span className="text-muted-foreground">Queries Run: </span>
            <span className="font-medium">{data.queriesRun}</span>
          </div>
        )}
        {data.filesAnalyzed !== undefined && (
          <div className="text-sm">
            <span className="text-muted-foreground">Files Analyzed: </span>
            <span className="font-medium">{data.filesAnalyzed}</span>
          </div>
        )}

        {/* Tier */}
        {data.tier && (
          <div className="text-sm">
            <span className="text-muted-foreground">Current Tier: </span>
            <span className="font-medium">{data.tier}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Upload guidance content component - inline conversational style
const UploadGuidanceContent: React.FC<{
  onOpenHelper: (method?: 'direct' | 'url' | 's3' | 'sql') => void;
}> = ({ onOpenHelper }) => {
  return (
    <div className="w-full space-y-3">
      <p className="text-sm">I can help you upload data. Choose how you'd like to proceed:</p>
      <div className="space-y-2">
        <button
          onClick={() => onOpenHelper('direct')}
          className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-background/50 transition-colors text-left group"
        >
          <Upload className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
          <div>
            <div className="text-sm font-medium group-hover:text-primary">Direct Upload</div>
            <div className="text-xs text-muted-foreground">Drag & drop or browse files (up to 5GB)</div>
          </div>
        </button>
        <button
          onClick={() => onOpenHelper('url')}
          className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-background/50 transition-colors text-left group"
        >
          <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
          <div>
            <div className="text-sm font-medium group-hover:text-primary">From URL</div>
            <div className="text-xs text-muted-foreground">Download from Dropbox, Google Drive, or direct link</div>
          </div>
        </button>
        <button
          onClick={() => onOpenHelper('s3')}
          className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-background/50 transition-colors text-left group"
        >
          <Database className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
          <div>
            <div className="text-sm font-medium group-hover:text-primary">S3 Transfer</div>
            <div className="text-xs text-muted-foreground">Transfer from your AWS S3 bucket</div>
          </div>
        </button>
        <button
          onClick={() => onOpenHelper('sql')}
          className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-background/50 transition-colors text-left group"
        >
          <Database className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
          <div>
            <div className="text-sm font-medium group-hover:text-primary">SQL Database</div>
            <div className="text-xs text-muted-foreground">Import from MySQL, PostgreSQL, MariaDB</div>
          </div>
        </button>
      </div>
    </div>
  );
};

// File selection content component - inline clickable file list
const FileSelectionContent: React.FC<{
  data: FileSelectionResult;
  onSelectFile?: (name: string, key: string) => void;
  onSendMessage?: (message: string) => void;
}> = ({ data, onSelectFile, onSendMessage }) => {
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleFileClick = (file: { name: string; s3Key: string }) => {
    // Select the file
    if (onSelectFile) {
      onSelectFile(file.name, file.s3Key);
    }

    // Send the follow-up command (e.g., "visualize as bars" with the file now selected)
    if (onSendMessage && data.action === 'visualize' && data.layout) {
      setTimeout(() => {
        onSendMessage(`visualize as ${data.layout}${data.groupBy ? ` by ${data.groupBy}` : ''}`);
      }, 100);
    }
  };

  return (
    <div className="w-full">
      <div className="space-y-1">
        {data.files.map((file, index) => (
          <button
            key={index}
            onClick={() => handleFileClick(file)}
            className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-background/50 transition-colors text-left group"
          >
            {file.isFolder ? (
              <Folder className="h-4 w-4 text-primary flex-shrink-0" />
            ) : (
              <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0 group-hover:text-primary" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate group-hover:text-primary">
                {file.name}
              </div>
              <div className="text-xs text-muted-foreground">
                {formatFileSize(file.size)}
              </div>
            </div>
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-2">
        Click a file to select and {data.action}
      </p>
    </div>
  );
};

export default ChatMessage;
